import React, { useEffect } from 'react';
import { autoId, passwordRegex, formFieldErrorPropsV2 } from '@workday/upc-common';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import styles from './change-password-entry.module.scss';
import {
    ID_CANCEL_BUTTON,
    ID_CURRENT_PASSWORD,
    ID_NEW_PASSWORD,
    ID_CONFIRM_PASSWORD,
    ID_PASSWORD_BUTTON
} from '../../../../autoIds';
import { useChangePasswordMutation } from '../../../../slices/account-api.slice';
import { useTranslation } from 'react-i18next';
import { Popper } from '@workday/canvas-kit-react/popup';
import { SystemIcon, SystemIconCircleSize, Flex } from '@workday/canvas-kit-react';
import { colors } from '@workday/canvas-kit-react/tokens';
import { checkCircleIcon } from '@workday/canvas-system-icons-web';
import { useForm } from 'react-hook-form';
import { PasswordFormField } from '../../../../shared/password-form-field/password-form-field';
import { StrongPasswordCheck } from '../../../../shared/passwordTextFieldWithConfirm/strong-password-check';

interface ChangePasswordEntryProps {
    onClose: () => void;
}

const validPassword = password => passwordRegex.test(password);

type ChangePasswordForm = {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
};

const idCurrentPassword = 'currentPassword';
const idNewPassword = 'newPassword';
const idConfirmPassword = 'confirmPassword';

export function ChangePasswordEntry({ onClose }: ChangePasswordEntryProps) {
    const { t } = useTranslation('account');

    const [changePassword, { isLoading, isSuccess }] = useChangePasswordMutation();

    const { register, handleSubmit, formState, watch, getValues } = useForm<ChangePasswordForm>({
        mode: 'onSubmit',
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        }
    });

    const [newPasswordWatch, confirmPasswordWatch] = watch([idNewPassword, idConfirmPassword]);

    const { ref: currentPasswordInputRef, ...currentPasswordInputProps } = register(idCurrentPassword, {
        required: t('Required.fieldGeneric', { ns: 'shared' }),
        deps: [idNewPassword]
    });

    const { ref: newPasswordInputRef, ...newPasswordInputProps } = register(idNewPassword, {
        required: t('Required.fieldGeneric', { ns: 'shared' }),
        validate: {
            validPassword,
            matchesCurrentPassword: value => {
                // use getValues here as using the watch value causes trouble
                return value !== getValues(idCurrentPassword)
                    ? true
                    : t('Required.fieldCurrentAndNewPasswordCantBeSame', { ns: 'shared' });
            }
        }
    });

    const { ref: confirmPasswordInputRef, ...confirmPasswordInputProps } = register(idConfirmPassword, {
        required: t('Required.fieldGeneric', { ns: 'shared' }),
        validate: {
            matchesNewPassword: value => {
                // use getValues here as using the watch value causes trouble
                return value === getValues(idNewPassword)
                    ? true
                    : t('Required.fieldNewAndConfirmedPasswordDontMatch', { ns: 'shared' });
            }
        }
    });

    useEffect(() => {
        if (isSuccess) {
            onClose();
        }
    }, [onClose, isSuccess]);

    const onSubmit = data => {
        changePassword({ oldPassword: data[idCurrentPassword], newPassword: data[idNewPassword] });
    };

    const showPopper = !!newPasswordWatch.length && !validPassword(newPasswordWatch);

    const showMatchMessage =
        !formState.errors.newPassword && newPasswordWatch.length > 0 && newPasswordWatch === confirmPasswordWatch;

    return (
        <div className={styles.changePasswordEntry}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1>{t('ChangePasswordEntry.changePassword')}</h1>
                <h2>{t('ChangePasswordEntry.pleaseEnter')}</h2>
                <PasswordFormField
                    label={t('ChangePasswordEntry.currentPasswordLabel')}
                    formFieldErrorProps={formFieldErrorPropsV2(
                        formState.errors.currentPassword,
                        undefined,
                        t('Required.fieldErrorLabel', { ns: 'shared' })
                    )}
                    passwordTextInputProps={{
                        testId: ID_CURRENT_PASSWORD,
                        inputRef: currentPasswordInputRef,
                        inputProps: currentPasswordInputProps,
                        placeholder: t('ChangePasswordEntry.currentPasswordInputPlaceholder'),
                        disabled: isLoading,
                        autoComplete: 'current-password',
                        autoFocus: true
                    }}
                />
                <PasswordFormField
                    label={t('ChangePasswordEntry.newPasswordFieldLabel')}
                    formFieldErrorProps={formFieldErrorPropsV2(
                        formState.errors.newPassword,
                        undefined,
                        t('Required.fieldErrorLabel', { ns: 'shared' })
                    )}
                    passwordTextInputProps={{
                        testId: ID_NEW_PASSWORD,
                        inputRef: newPasswordInputRef,
                        inputProps: newPasswordInputProps,
                        placeholder: t('ChangePasswordEntry.newPasswordInputPlaceholder'),
                        disabled: isLoading,
                        autoComplete: 'new-password',
                        autoFocus: false
                    }}
                />
                <Popper open={showPopper} portal={false}>
                    <StrongPasswordCheck password={newPasswordWatch} />
                </Popper>
                <PasswordFormField
                    label={t('ChangePasswordEntry.confirmPasswordFieldLabel')}
                    formFieldErrorProps={formFieldErrorPropsV2(
                        formState.errors.confirmPassword,
                        undefined,
                        t('Required.fieldErrorLabel', { ns: 'shared' })
                    )}
                    passwordTextInputProps={{
                        testId: ID_CONFIRM_PASSWORD,
                        inputRef: confirmPasswordInputRef,
                        inputProps: confirmPasswordInputProps,
                        placeholder: t('ChangePasswordEntry.confirmNewPasswordInputPlaceholder'),
                        disabled: isLoading,
                        autoComplete: 'new-password',
                        autoFocus: false
                    }}
                />
                <div className={styles.successMessage}>
                    {showMatchMessage && (
                        <div>
                            <SystemIcon
                                className={styles.successIcon}
                                color={colors.blueberry500}
                                colorHover={colors.blueberry500}
                                icon={checkCircleIcon}
                                size={SystemIconCircleSize.s}
                            />
                            {t('ChangePasswordEntry.successPasswordsMatch')}
                        </div>
                    )}
                </div>
                <Flex flexDirection='column' alignItems='center'>
                    <PrimaryButton
                        {...autoId(ID_PASSWORD_BUTTON)}
                        size='large'
                        type='submit'
                        grow={true}
                        marginBottom='1rem'>
                        {t('ChangePasswordEntry.submitButton')}
                    </PrimaryButton>
                    <TertiaryButton {...autoId(ID_CANCEL_BUTTON)} type='button' size='large' onClick={onClose}>
                        {t('Common.cancel', { ns: 'shared' })}
                    </TertiaryButton>
                </Flex>
            </form>
        </div>
    );
}
